/**
 * Faturalar presenter'i
 * Fatura ekraninin render ve form etkileşimlerini yonetir.
 */

import { AppState } from '../../modules/state.js';
import { FirebaseService } from '../../shared/services/firebase.service.js';
import { COLLECTIONS, MONTHS, CATEGORY_LABELS } from '../../firebase-config.js';
import { formatNumber } from '../../shared/utils/formatters.js';

interface BillsPresenterDeps {
    openModal: (modalId: string) => void;
    showToast: (message: string, type?: string) => void;
    generateId: () => string;
    escapeHtml: (value: string) => string;
}

export function renderBillsPresenter(deps: BillsPresenterDeps): void {
    const container = document.getElementById('bills-list');
    if (!container) return;

    const yearFilter = (document.getElementById('bill-year-filter') as HTMLSelectElement).value;
    const filtered = AppState.bills.filter(bill => bill.year == yearFilter).sort((a, b) => b.month - a.month);

    if (filtered.length === 0) {
        container.innerHTML = '<p class="empty-state">Bu yıla ait fatura bulunamadı</p>';
        return;
    }

    const billIcons: Record<string, string> = {
        elektrik: '⚡',
        su: '💧',
        dogalgaz: '🔥',
        default: '📄'
    };

    const billLabels: Record<string, string> = {
        elektrik: 'Elektrik Faturası',
        su: 'Su Faturası',
        dogalgaz: 'Doğalgaz Faturası',
        default: 'Fatura'
    };

    const getPreviousBill = (currentBill: any) => {
        const candidates = AppState.bills
            .filter(bill => bill.type === currentBill.type && (bill.year < currentBill.year || (bill.year === currentBill.year && bill.month < currentBill.month)))
            .sort((a, b) => (b.year - a.year) || (b.month - a.month));
        return candidates[0];
    };

    container.innerHTML = filtered.map(bill => {
        const previousBill = getPreviousBill(bill);
        const difference = previousBill ? bill.amount - previousBill.amount : 0;
        const trendClass = difference > 0 ? 'expense' : difference < 0 ? 'income' : 'balance';
        const trendText = previousBill
            ? `${difference > 0 ? '+' : ''}₺${formatNumber(Math.abs(difference))} (${MONTHS[previousBill.month - 1]} ${previousBill.year})`
            : 'İlk kayıt';

        return `
        <div class="transaction-card expense">
            <div class="transaction-icon">
                ${billIcons[bill.type] || billIcons.default}
            </div>
            <div class="transaction-info">
                <div class="transaction-category">${billLabels[bill.type] || billLabels.default}</div>
                <div class="transaction-description">${bill.notes ? deps.escapeHtml(bill.notes) : `${MONTHS[bill.month - 1]} ${bill.year}`}</div>
                <div class="bill-trend ${trendClass}">Karşılaştırma: ${trendText}</div>
            </div>
            <div class="transaction-meta">
                <div class="transaction-amount expense">₺${formatNumber(bill.amount)}</div>
                <div class="transaction-date">📅 ${MONTHS[bill.month - 1]} ${bill.year}</div>
            </div>
            <div class="transaction-actions">
                ${bill.fileData ? `<button class="btn btn-secondary btn-sm" onclick="viewBill('${bill.id}')" title="Görüntüle">👁️</button>` : ''}
                <button class="btn btn-secondary btn-sm" onclick="editBill('${bill.id}')" title="Düzenle">✏️</button>
                <button class="btn btn-danger btn-sm" onclick="deleteBill('${bill.id}')" title="Sil">🗑️</button>
            </div>
        </div>
    `;
    }).join('');
}

export function renderResidentBillsPresenter(): void {
    const container = document.getElementById('resident-bills-list');
    if (!container) return;

    if (AppState.bills.length === 0) {
        container.innerHTML = '<p class="empty-state">Henüz fatura eklenmemiş</p>';
        return;
    }

    const sorted = [...AppState.bills].sort((a, b) => b.year !== a.year ? b.year - a.year : b.month - a.month);
    container.innerHTML = sorted.map(bill => `
        <div class="bill-card glass-card">
            <div class="bill-header">
                <span class="bill-month">${MONTHS[bill.month - 1]} ${bill.type ? `• ${CATEGORY_LABELS[bill.type] || bill.type}` : ''}</span>
                <span class="bill-year">${bill.year}</span>
            </div>
            ${((Date.now() - new Date(bill.year, bill.month - 1, 1).getTime()) / 86400000) <= 45 ? '<span class="resident-item-badge">Yeni</span>' : ''}
            <div class="bill-amount">₺${formatNumber(bill.amount)}</div>
            ${bill.fileData ? `<div class="bill-actions"><button class="btn btn-secondary btn-sm btn-full" onclick="viewBill('${bill.id}')">👁 Görüntüle</button></div>` : ''}
        </div>
    `).join('');
}

export function addBillPresenter(data: Record<string, any>, deps: BillsPresenterDeps): any {
    const bill = { id: deps.generateId(), ...data, createdAt: new Date().toISOString() };
    AppState.bills.push(bill);
    FirebaseService.add(COLLECTIONS.BILLS, bill);
    deps.showToast('Fatura eklendi', 'success');
    return bill;
}

export function updateBillPresenter(id: string, data: Record<string, any>, deps: BillsPresenterDeps): boolean {
    const index = AppState.bills.findIndex(bill => bill.id === id);
    if (index === -1) {
        return false;
    }

    const updated = { ...AppState.bills[index], ...data };
    AppState.bills[index] = updated;
    FirebaseService.save(COLLECTIONS.BILLS, id, updated);
    deps.showToast('Fatura güncellendi', 'success');
    return true;
}

export function deleteBillPresenter(id: string, deps: BillsPresenterDeps): void {
    if (!confirm('Bu faturayı silmek istediğinizden emin misiniz?')) return;

    AppState.bills = AppState.bills.filter(bill => bill.id !== id);
    FirebaseService.delete(COLLECTIONS.BILLS, id);
    renderBillsPresenter(deps);
    deps.showToast('Fatura silindi', 'success');
}

export function editBillPresenter(id: string, deps: BillsPresenterDeps): void {
    const bill = AppState.bills.find(item => item.id === id);
    if (!bill) return;

    document.getElementById('bill-modal-title')!.textContent = 'Fatura Düzenle';
    (document.getElementById('bill-id') as HTMLInputElement).value = bill.id;
    (document.getElementById('bill-type') as HTMLSelectElement).value = bill.type || 'elektrik';
    (document.getElementById('bill-month') as HTMLSelectElement).value = bill.month.toString();
    (document.getElementById('bill-year') as HTMLSelectElement).value = bill.year.toString();
    (document.getElementById('bill-amount') as HTMLInputElement).value = bill.amount.toString();
    (document.getElementById('bill-notes') as HTMLTextAreaElement).value = bill.notes || '';
    deps.openModal('bill-modal');
}

export function viewBillPresenter(id: string, deps: BillsPresenterDeps): void {
    const bill = AppState.bills.find(item => item.id === id);
    if (!bill || !bill.fileData) return;

    const viewer = document.getElementById('bill-viewer');
    if (!viewer) return;

    viewer.innerHTML = `
        <div class="bill-info">
            <p><strong>Dönem:</strong> ${MONTHS[bill.month - 1]} ${bill.year}</p>
            <p><strong>Tutar:</strong> ₺${formatNumber(bill.amount)}</p>
        </div>
        ${bill.fileType === 'pdf' ? `<iframe src="${bill.fileData}" title="Fatura"></iframe>` : `<img src="${bill.fileData}" alt="Fatura" />`}
    `;
    deps.openModal('view-bill-modal');
}
